/**
 * 결정적 빠른 수정 — 결정 #30. AI를 쓰기 전에, **실행 에러 메시지**가 가리키는 실패
 * 심볼을 알려진 capability API와 편집거리로 매칭해 오타를 무료로 교정한다. 소스를
 * 훑지 않고 "실제로 터진 심볼"만 건드리므로 오탐(멀쩡한 코드 손대기)이 없다.
 *
 * 한계(정직): 로직 오류(안 터지는 버그)나 사용자 정의 변수 오타는 의도를 알 수 없어
 * 결정적으로 못 고친다 → 그건 AI/사람 몫. 여기선 "고칠 방법이 명백한" 부류만 다룬다.
 *
 * 플랫폼 중립(결정 #10): 순수 함수. 실제 실행/재시도는 web 러너가 오케스트레이션한다.
 */

/** 스크립트 capability API 표면(결정 #4/#17). 오타 교정의 매칭 대상. */
export const KNOWN_SCRIPT_GLOBALS = [
  "setResult",
  "getVar",
  "setVar",
  "getChatMessages",
  "log",
  "random",
  "now",
  "fetch",
  "storageGet",
  "storageSet",
  "generateImage",
  "sendPrompt",
  "context",
] as const;

/** Levenshtein 편집 거리(순수). */
export function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array<number>(n + 1);
  let cur = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    cur[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + cost);
    }
    [prev, cur] = [cur, prev];
  }
  return prev[n]!;
}

/**
 * 실행 에러 메시지에서 "정의되지 않음/nil로 호출/인덱싱"된 심볼 이름을 뽑는다(JS·Lua).
 * 못 찾으면 null. 예:
 *  - JS: "setResults is not defined" → "setResults"
 *  - Lua: "attempt to call a nil value (global 'setResults')" → "setResults"
 */
export function extractFailedSymbol(errorMessage: string): string | null {
  // JS ReferenceError.
  const js = /(\b[A-Za-z_$][\w$]*) is not defined/.exec(errorMessage);
  if (js) return js[1]!;
  // Lua: global '<name>' (call/index a nil value).
  const lua = /\bglobal '([A-Za-z_]\w*)'/.exec(errorMessage);
  if (lua) return lua[1]!;
  return null;
}

export interface IdentifierFix {
  /** 잘못된(터진) 심볼. */
  from: string;
  /** 교정 후보(알려진 API). */
  to: string;
  /** 편집 거리(신뢰도 참고). */
  distance: number;
}

/**
 * 잘못된 심볼을 알려진 이름들과 비교해 **유일하게 가장 가까운** 후보를 고른다. 동률이
 * 여럿이거나(모호) 거리가 임계 초과면 null(추측하지 않음 — 조용한 오작동 방지).
 *
 * 임계: 거리 ≤ 2 이며 심볼 길이보다 작아야 한다(너무 짧은 건 매칭 안 함). 대소문자만
 * 다른 경우(getvar↔getVar)는 거리 1~2라 잘 잡힌다.
 */
export function suggestIdentifier(
  bad: string,
  known: readonly string[] = KNOWN_SCRIPT_GLOBALS,
): IdentifierFix | null {
  if (!bad || bad.length < 2) return null;
  // 이미 알려진 이름이면 오타가 아님.
  if (known.includes(bad)) return null;

  let best: { name: string; dist: number } | null = null;
  let tie = false;
  for (const name of known) {
    const d = editDistance(bad, name);
    if (best === null || d < best.dist) {
      best = { name, dist: d };
      tie = false;
    } else if (d === best.dist) {
      tie = true;
    }
  }
  if (!best) return null;
  const maxDist = bad.length <= 4 ? 1 : 2;
  if (best.dist > maxDist || best.dist >= bad.length) return null;
  if (tie) return null; // 후보가 모호하면 고치지 않는다.
  return { from: bad, to: best.name, distance: best.dist };
}

/**
 * 소스에서 식별자 `from`을 `to`로 치환한다(단어 경계 기준). 식별자 토큰만 바꾸므로
 * 부분 문자열 오염이 없다(예: setResult가 setResultX의 일부로 안 바뀜). 치환이 한 건도
 * 없으면 null(적용할 게 없음).
 */
export function applyIdentifierFix(source: string, from: string, to: string): string | null {
  const re = new RegExp(`(?<![\\w$])${escapeRegExp(from)}(?![\\w$])`, "g");
  if (!re.test(source)) return null;
  return source.replace(new RegExp(`(?<![\\w$])${escapeRegExp(from)}(?![\\w$])`, "g"), to);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export interface QuickFixResult {
  fixed: string;
  fix: IdentifierFix;
  /** 사람이 읽는 설명. */
  description: string;
}

/**
 * 에러 메시지 하나로 빠른 수정을 시도한다(결정 #30). 실패 심볼 추출 → 후보 매칭 →
 * 식별자 교정까지 한 번에. 고칠 수 없으면 null(이 경우 AI/사람에게 넘긴다).
 */
export function quickFixFromError(
  source: string,
  errorMessage: string,
  known: readonly string[] = KNOWN_SCRIPT_GLOBALS,
): QuickFixResult | null {
  const symbol = extractFailedSymbol(errorMessage);
  if (!symbol) return null;
  const fix = suggestIdentifier(symbol, known);
  if (!fix) return null;
  const fixed = applyIdentifierFix(source, fix.from, fix.to);
  if (fixed === null || fixed === source) return null;
  return {
    fixed,
    fix,
    description: `'${fix.from}' → '${fix.to}' (오타로 추정)`,
  };
}
